import Image from 'next/legacy/image';
import { useContext, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { captureException } from '@sentry/browser';
import * as _ from 'lodash-es';
import Link from 'next/link';
import { useDispatch } from 'react-redux';

import { useIsViewedVin } from 'lib/hooks/useIsViewedVin';
import useSubmittedDI, { LeadSubmission } from 'lib/hooks/useSubmitted';
import { VehicleV2 } from 'lib/AutolistAPI';
import favoriteToggleEvent from 'lib/Tracking/Srp/SearchResultCard/FavoriteToggleEvent';
import useEvents from 'lib/hooks/useEvents';
import useFavorite from 'lib/hooks/useFavorite';
import { VehicleTap } from 'lib/Tracking/Srp/VehicleTap';
import { useLeadByVin } from 'lib/hooks/useLeadByVin';
import useHashLocation from 'lib/hooks/useHashLocation';
import { useIsQuickContactEnabled } from 'lib/hooks/useQuickContact';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import { useQuickLeadSubmitter } from 'lib/hooks/useLeadSender';
import { useAlert } from 'lib/hooks/useAlert';
import { Features, SourcePages } from 'lib/EventSchema';

import ViewedBadge from 'components/Srp/VehicleCardBadge/ViewedBadge';
import PriceDropBadge from 'components/Srp/VehicleCardBadge/PriceDropBadge';
import FreshBadge from 'components/Srp/VehicleCardBadge/FreshBadge';
import HotCarBadge from 'components/Srp/VehicleCardBadge/HotCarBadge';

import locationIcon from 'public/static/images/srp/Location.svg';
import down from 'public/static/images/srp/GreenDown.svg';
import up from 'public/static/images/srp/UpIcon.svg';
import clock from 'public/static/images/srp/TimeIcon.svg';
import noImages from 'public/static/images/srp/no-images.png';
import noData from 'public/static/images/srp/NoDataIcon.svg';

import { setSrpScrollPosition, setSrpScrolledToBottom } from 'redux-implementations/vdpScroll';
import { LeadTrackingPageSource } from 'types/Apis/LeadTrackingParams';
import { trackLeadSuccess } from 'lib/Tracking/LeadForm/FormSuccess';
import SrpEventContext from 'contexts/SrpEventContext';
import styles, { useStyles } from './styles';
import ContactCta from '../ContactCta';
import FavoriteIcon from '../../FavoriteIcon';
import { formatMileage } from './utils';
import Price from './price';
import SrpTileCta from '../SrpTile/SrpTileCta';

function DescriptionList({ vehicle }: { vehicle: VehicleV2 }): JSX.Element {
  return (
    <dl className="property-list" style={{ display: 'none' }}>
      <dt>Year: </dt>
      <dd>{vehicle.year} </dd>
      <dt>Make: </dt>
      <dd>{vehicle.make} </dd>
      <dt>Model: </dt>
      <dd>{vehicle.model} </dd>
      <dt>Body type: </dt>
      <dd>{vehicle.bodyType} </dd>
      <dt>Doors: </dt>
      <dd>{vehicle.doorCount} </dd>
      <dt>Drivetrain: </dt>
      <dd>{vehicle.driveline} </dd>
      <dt>Exterior color: </dt>
      <dd>{vehicle.normalizedColorExterior} </dd>
      <dt>Combined gas mileage: </dt>
      <dd>{vehicle.combinedMpg} </dd>
      <dt>Fuel type: </dt>
      <dd>{vehicle.fuelType} </dd>
      <dt>Interior color: </dt>
      <dd>{vehicle.normalizedColorInterior} </dd>
      <dt>Transmission: </dt>
      <dd>{vehicle.transmission} </dd>
      <dt>Mileage: </dt>
      <dd>{vehicle.mileage} </dd>
      <dt>VIN: </dt>
      <dd>{vehicle.vin} </dd>
    </dl>
  );
}

function PriceComparison({
  relativePriceDiff,
  isMobile = false,
}: {
  relativePriceDiff: number;
  isMobile?: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={`price-comparison-container${isMobile ? '-mobile' : ''}`}>
      <img src={relativePriceDiff > 0 ? up : down} alt="" />
      <div
        className={`price-comparison-text${isMobile ? '-mobile' : ''} ${
          relativePriceDiff.toString().length > 2 ? 'large-price-difference' : ''
        }`}
      >
        <span
          className={classNames(
            'price-comparison',
            `${relativePriceDiff > 0 ? 'negative' : 'positive'}`
          )}
        >
          {relativePriceDiff.toString().replace(/[x^+-]/, '')}%
        </span>{' '}
        {relativePriceDiff > 0 ? t('srp.buyerIntelligence.more') : t('srp.buyerIntelligence.less')}{' '}
        {t('srp.buyerIntelligence.thanSimilar')}
      </div>
      <style jsx>{styles}</style>
    </div>
  );
}

export interface SearchResultCardProps {
  prioritizeImage: boolean;
  onClickContact: () => void;
  onQuickLeadSucceeded?: () => void;
  vehicle: VehicleV2;
  useSubmitted?: (vin: string) => LeadSubmission;
  onClickMonthlyPaymentCalculator: () => void;
  onOpenSrpTile: (vin: string) => void;
  listingIndex: number;
}

function SearchResultCard({
  prioritizeImage = false,
  onClickContact,
  onQuickLeadSucceeded,
  vehicle,
  useSubmitted = useSubmittedDI,
  onClickMonthlyPaymentCalculator,
  onOpenSrpTile,
  listingIndex,
}: SearchResultCardProps): JSX.Element {
  const { t } = useTranslation();
  const { favorite, isFavorited, unfavorite } = useFavorite(vehicle.vin);
  const classes = useStyles();

  const {
    vin,
    hasImages,
    images,
    make,
    model,
    year,
    location,
    mileage,
    showNewMileage,
    price,
    isHot,
    hasRecentPriceDrop,
    buyerIntelligence,
    justListed,
    trim,
    isRegional,
    availableNationwide,
    carmax,
    vdpUrl: initialVdpUrl,
  } = vehicle;

  const leadSubmission = useSubmitted(vin);
  const existingLead = useLeadByVin(vehicle.vin);
  const isViewed = useIsViewedVin();
  const quickContactEnabled = useIsQuickContactEnabled();
  const logger = useEvents();
  const dispatch = useDispatch();
  const { trackingParams } = vehicle;
  const [vdpUrl, setVdpUrl] = useState(initialVdpUrl);

  const { showError } = useAlert();
  const { isMobile, isTablet } = useDeviceScreen();
  const contacted = leadSubmission.isSubmitted || !!existingLead;
  const [favoriteState, toggleFavoriteState] = useReducer((isFav) => !isFav, isFavorited);
  const { values, cleanKeys } = useHashLocation();
  const [quickLeadSubmitLoading, setQuickleadSubmitLoading] = useState(false);
  const { sourcePageCategory } = useContext(SrpEventContext);

  useEffect(() => {
    if (isFavorited && !favoriteState) {
      toggleFavoriteState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFavorited]);

  useEffect(() => {
    setVdpUrl(
      `${window.location.origin}${window.location.pathname}#${cleanKeys({
        ...values,
        vin,
      })}`
    );
  }, [cleanKeys, values, vin]);

  function selectVehicleBadge(): JSX.Element | null {
    if (isViewed) return <ViewedBadge />;
    if (isHot) return <HotCarBadge />;
    if (hasRecentPriceDrop) return <PriceDropBadge />;
    if (justListed) return <FreshBadge />;
    return null;
  }

  const qcSourcePage =
    isMobile || isTablet ? LeadTrackingPageSource.MobileSrp : LeadTrackingPageSource.DesktopSrp;
  const quickLeadSender = useQuickLeadSubmitter(
    Features.leadForm,
    qcSourcePage,
    SourcePages.SrpLeadForm,
    sourcePageCategory
  );

  const imageUrl = hasImages ? images[0] : noImages;

  function onToggleFavorite(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    toggleFavoriteState();
    e.preventDefault();
    e.stopPropagation();
    if (isFavorited) {
      unfavorite();
    } else favorite();

    logger.log(favoriteToggleEvent({ isFavorited, trackingParams, vin, listingIndex }));
  }

  function onToggleSrpTile(): void {
    onOpenSrpTile(vin);
  }

  async function handleQuickLead(): Promise<void> {
    try {
      setQuickleadSubmitLoading(true);
      const { leadResponse, leadPayload } = await quickLeadSender({
        vehicle,
        sendAlerts: true,
      });

      trackLeadSuccess({
        vehicle,
        contactInfo: leadPayload.contactInfo,
        zip: leadPayload.contactInfo?.zip,
        leadResponse,
        geolocation: leadPayload.geolocation,
        cplValue: vehicle?.cplValue,
      });

      favorite();
      setQuickleadSubmitLoading(false);
      onQuickLeadSucceeded?.();
    } catch (e) {
      captureException(e);
      console.error(e);
      showError(t('errors.generic'), { debug: _.toPlainObject(e) });
    }
  }

  function onContactCtaClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    if (quickContactEnabled) {
      handleQuickLead();
    } else onClickContact();
    e.stopPropagation();
  }

  function renderDealerName(): string {
    if (availableNationwide) {
      return t('srp.displayLocation.nationwide');
    }
    if (carmax) {
      return t('srp.displayLocation.transferAvailable');
    }
    if (isRegional) {
      return t('srp.displayLocation.regional');
    }

    return '';
  }

  function timeOnMarket(days: number | null): string | null | undefined {
    switch (days) {
      case 0:
        return null;
      case 1:
        return t('srp.buyerIntelligence.dayOnMarket');
      default:
        return t('srp.buyerIntelligence.daysOnMarket');
    }
  }

  function onClickDetailsAnchor(): void {
    logger.log(VehicleTap({ vin, listingIndex }));
    const wrapperElement = document.getElementById('srp-wrapper');
    const scrollPosition = Math.floor(wrapperElement?.getBoundingClientRect().bottom as number);
    const scrolledToBottom = scrollPosition <= window.innerHeight;
    dispatch(setSrpScrollPosition(window?.scrollY));
    dispatch(setSrpScrolledToBottom(scrolledToBottom));
  }

  const isTimeOnMarketEmpty =
    buyerIntelligence.timeOnMarket === null || buyerIntelligence.timeOnMarket === undefined;

  return (
    <div className="search-result" data-testid="search-result">
      <Link passHref href={vdpUrl} legacyBehavior>
        <a
          className="details"
          href="replace"
          data-testid="details-anchor"
          onClick={onClickDetailsAnchor}
        >
          <div className={classes.imageContainer}>
            <Image
              objectFit="cover"
              src={imageUrl}
              key={imageUrl}
              width={817}
              height={487}
              alt={`${vehicle.make} ${vehicle.year} ${vehicle.model}`}
              priority={prioritizeImage}
            />
            <FavoriteIcon onClick={onToggleFavorite} active={favoriteState} />
            <div className="badge-container-mobile">{selectVehicleBadge()}</div>
          </div>
          {isMobile && !!buyerIntelligence.relativePriceDiff && (
            <PriceComparison relativePriceDiff={buyerIntelligence.relativePriceDiff} isMobile />
          )}
          <div className="vehicle-details">
            <div className="left">
              <div className="year-make-model">
                {year} {make} {model}
                {trim && <>&nbsp;{trim}</>}
              </div>
              <div className="miles-location">
                <div className="mileage">
                  {showNewMileage ? t('srp.new') : formatMileage(mileage || 0)}
                </div>
                <div className="location">{location}</div>
              </div>
              <div className="dealer-location">
                {renderDealerName() && (
                  <>
                    <img src={locationIcon} alt="" />
                    <div className="dealer-name">{renderDealerName()}</div>
                  </>
                )}
              </div>
              <div className="time-on-market-container-mobile">
                <img src={clock} alt="" />
                {!isTimeOnMarketEmpty && (
                  <>
                    <div className="time-on-market">
                      {buyerIntelligence.timeOnMarket === 0
                        ? t('srp.buyerIntelligence.newListing')
                        : buyerIntelligence.timeOnMarket}
                    </div>
                    {timeOnMarket(buyerIntelligence.timeOnMarket)}
                  </>
                )}
                {isTimeOnMarketEmpty && <div className="no-data">--</div>}
              </div>
            </div>
            <div className="buyer-intelligence">
              {!!buyerIntelligence.relativePriceDiff && (
                <PriceComparison relativePriceDiff={buyerIntelligence.relativePriceDiff} />
              )}
              {!buyerIntelligence.relativePriceDiff && (
                <div className="no-data-container">
                  <Image src={noData} alt="" width={20} height={30} />
                  <div className="no-data">--</div>
                </div>
              )}
              <div className="time-on-market-container">
                <Image src={clock} alt="" width={18} height={30} />
                {!isTimeOnMarketEmpty && (
                  <>
                    <div className="time-on-market">
                      {buyerIntelligence.timeOnMarket === 0
                        ? t('srp.buyerIntelligence.newListing')
                        : buyerIntelligence.timeOnMarket}
                    </div>
                    {timeOnMarket(buyerIntelligence.timeOnMarket)}
                  </>
                )}
                {isTimeOnMarketEmpty && <div className="no-data">--</div>}
              </div>
            </div>
            <div className="right">
              <Price
                price={price}
                onClickMonthlyPaymentCalculator={onClickMonthlyPaymentCalculator}
              />

              <div className="badge-container">{selectVehicleBadge()}</div>
            </div>
            {!isMobile && (
              <SrpTileCta onClick={onToggleSrpTile} vin={vin} listingIndex={listingIndex} />
            )}
          </div>
        </a>
      </Link>

      <div className="contact-cta-container">
        <ContactCta
          className="contact-cta-mobile"
          data-testid="contact-cta-mobile"
          contacted={contacted}
          renderLoadingSpinner={quickLeadSubmitLoading}
          quickContactEnabled={quickContactEnabled}
          onClick={onContactCtaClick}
        />
        <ContactCta
          className="contact-cta-desktop"
          data-testid="contact-cta-desktop"
          contacted={contacted}
          renderLoadingSpinner={quickLeadSubmitLoading}
          quickContactEnabled={quickContactEnabled}
          onClick={onContactCtaClick}
        />
      </div>

      {isMobile && (
        <SrpTileCta onClick={onToggleSrpTile} isMobile vin={vin} listingIndex={listingIndex} />
      )}

      <DescriptionList vehicle={vehicle} />
      <style jsx>{styles}</style>
    </div>
  );
}

export default SearchResultCard;
